import { supabase } from './client'

type UserType = 'patient' | 'provider'
const REQUEST_TIMEOUT_MS = 15000

let verifiedResetTokens: Partial<Record<UserType, string>> = {}

const RESET_TOKEN_STORAGE_KEY_PREFIX = 'homicareplus.passwordReset.resetToken.'
const RESET_TOKEN_VERIFIED_AT_STORAGE_KEY_PREFIX = 'homicareplus.passwordReset.verifiedAt.'
const RESET_TOKEN_TTL_MS = 10 * 60 * 1000

function canUseSessionStorage() {
  return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined'
}


function getStorageKey(prefix: string, userType: UserType) {
  return `${prefix}${userType}`
}

function persistVerifiedResetToken(userType: UserType, resetToken: string) {
  verifiedResetTokens[userType] = resetToken

  if (!canUseSessionStorage()) return
  try {
    window.sessionStorage.setItem(getStorageKey(RESET_TOKEN_STORAGE_KEY_PREFIX, userType), resetToken)
    window.sessionStorage.setItem(getStorageKey(RESET_TOKEN_VERIFIED_AT_STORAGE_KEY_PREFIX, userType), String(Date.now()))
  } catch {
    // best-effort (private browsing / storage disabled)
  }
}

function getPersistedVerifiedResetToken(userType: UserType): { token: string | null; verifiedAtMs: number | null } {
  const inMemoryToken = verifiedResetTokens[userType]
  if (inMemoryToken) return { token: inMemoryToken, verifiedAtMs: null }

  if (!canUseSessionStorage()) return { token: null, verifiedAtMs: null }

  try {
    const token = window.sessionStorage.getItem(getStorageKey(RESET_TOKEN_STORAGE_KEY_PREFIX, userType))
    const verifiedAtRaw = window.sessionStorage.getItem(getStorageKey(RESET_TOKEN_VERIFIED_AT_STORAGE_KEY_PREFIX, userType))
    const verifiedAtMs = verifiedAtRaw ? Number(verifiedAtRaw) : null
    return { token, verifiedAtMs: Number.isFinite(verifiedAtMs) ? verifiedAtMs : null }
  } catch {
    return { token: null, verifiedAtMs: null }
  }
}

async function getInvokeErrorMessage(params: {
  functionName: string
  error: unknown
  response?: unknown
  fallbackMessage: string
}): Promise<string> {
  const { functionName, error, response, fallbackMessage } = params

  const status = (response as { status?: number } | undefined)?.status
  const statusPrefix = typeof status === 'number' ? `[${functionName} ${status}] ` : ''

  // Try to read a structured error from response body.
  try {
    const responseWithText = response as { text?: () => Promise<string> } | undefined
    if (responseWithText?.text) {
      const raw = await responseWithText.text()
      const parsed = raw ? (JSON.parse(raw) as unknown) : null
      if (parsed && typeof parsed === 'object' && 'error' in (parsed as Record<string, unknown>)) {
        const serverError = (parsed as Record<string, unknown>).error
        if (typeof serverError === 'string' && serverError.trim().length > 0) {
          return `${statusPrefix}${serverError}`
        }
      }
      if (raw && raw.trim().length > 0) {
        return `${statusPrefix}${raw}`
      }
    }
  } catch {
    // ignore
  }

  if (error instanceof Error && error.message) {
    return `${statusPrefix}${error.message}`
  }

  return `${statusPrefix}${fallbackMessage}`
}

async function withTimeout<T>(promiseLike: PromiseLike<T>, timeoutMs: number, message: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs)
  })

  try {
    return await Promise.race([Promise.resolve(promiseLike), timeoutPromise])
  } finally {
    if (timeoutId) clearTimeout(timeoutId)
  }
}

export const passwordResetService = {
  async requestOtp(email: string, userType: UserType): Promise<{ success: boolean; error?: string }> {
    try {
      const cleanEmail = email.trim().toLowerCase()
      const { data, error, response } = await withTimeout(supabase.functions.invoke('password-reset-otp', {
        body: {
          action: 'request',
          email: cleanEmail,
          userType,
        },
      }), REQUEST_TIMEOUT_MS, 'Password reset request timed out. Please try again.')

      if (error) {
        console.error('Password reset OTP request function error:', error)
        return {
          success: false,
          error: await getInvokeErrorMessage({
            functionName: 'password-reset-otp',
            error,
            response,
            fallbackMessage: 'Unable to send reset code. Please try again.',
          }),
        }
      }

      if (data?.error) {
        return { success: false, error: data.error }
      }

      return { success: true }
    } catch (error) {
      console.error('Password reset OTP request error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to initiate password reset',
      }
    }
  },

  async verifyOtp(email: string, code: string, userType: UserType): Promise<{ success: boolean; error?: string }> {
    try {
      const cleanEmail = email.trim().toLowerCase()
      const cleanCode = code.trim()
      const { data, error, response } = await withTimeout(supabase.functions.invoke('password-reset-otp', {
        body: {
          action: 'verify',
          email: cleanEmail,
          code: cleanCode,
          userType,
        },
      }), REQUEST_TIMEOUT_MS, 'Verification request timed out. Please try again.')

      if (error) {
        console.error('Password reset OTP verify function error:', error)
        return {
          success: false,
          error: await getInvokeErrorMessage({
            functionName: 'password-reset-otp',
            error,
            response,
            fallbackMessage: 'Unable to verify code. Please try again.',
          }),
        }
      }

      if (data?.error) {
        return { success: false, error: data.error }
      }

      if (typeof data?.resetToken !== 'string' || data.resetToken.length === 0) {
        const reportedSuccess = !!(data && typeof data === 'object' && 'success' in (data as Record<string, unknown>) && (data as Record<string, unknown>).success === true)
        if (reportedSuccess) {
          console.error('Password reset verify succeeded but resetToken missing:', data)
          return {
            success: false,
            error: 'Reset verified, but server did not return a reset token. Please ask an admin to redeploy the `password-reset-otp` function and try again.',
          }
        }

        return { success: false, error: 'Reset verification failed. Please request a new code.' }
      }

      persistVerifiedResetToken(userType, data.resetToken)

      return { success: true }
    } catch (error) {
      console.error('Password reset OTP verification error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to verify code',
      }
    }
  },

  async resetPassword(email: string, newPassword: string, userType: UserType): Promise<{ success: boolean; error?: string }> {
    try {
      const cleanEmail = email.trim().toLowerCase()
      const persisted = getPersistedVerifiedResetToken(userType)
      const resetToken = persisted.token

      if (!resetToken) {
        return { success: false, error: 'Please verify your reset code again.' }
      }

      if (persisted.verifiedAtMs && Date.now() - persisted.verifiedAtMs > RESET_TOKEN_TTL_MS) {
        return { success: false, error: 'Reset session expired. Please verify a new code.' }
      }

      const { data, error, response } = await withTimeout(supabase.functions.invoke('reset-password', {
        body: {
          email: cleanEmail,
          newPassword,
          userType,
          resetToken,
        },
      }), REQUEST_TIMEOUT_MS, 'Password reset timed out. Please try again.')

      if (error) {
        console.error('Password reset function invocation error:', error)
        const status = (response as { status?: number } | undefined)?.status
        if (status === 429) {
          return { success: false, error: 'Too many reset attempts. Please wait a bit and try again.' }
        }
        return {
          success: false,
          error: await getInvokeErrorMessage({
            functionName: 'reset-password',
            error,
            response,
            fallbackMessage: 'Failed to reset password',
          }),
        }
      }

      if (data?.error) {
        return { success: false, error: data.error }
      }

      delete verifiedResetTokens[userType]
      if (canUseSessionStorage()) {
        try {
          window.sessionStorage.removeItem(getStorageKey(RESET_TOKEN_STORAGE_KEY_PREFIX, userType))
          window.sessionStorage.removeItem(getStorageKey(RESET_TOKEN_VERIFIED_AT_STORAGE_KEY_PREFIX, userType))
        } catch {
          // ignore
        }
      }

      return { success: true }
    } catch (error) {
      console.error('Password reset error:', error)
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to reset password',
      }
    }
  },

  clearVerifiedResetToken(userType: UserType) {
    delete verifiedResetTokens[userType]
    if (!canUseSessionStorage()) return
    try {
      window.sessionStorage.removeItem(getStorageKey(RESET_TOKEN_STORAGE_KEY_PREFIX, userType))
      window.sessionStorage.removeItem(getStorageKey(RESET_TOKEN_VERIFIED_AT_STORAGE_KEY_PREFIX, userType))
    } catch {
      // ignore
    }
  },
}